/**
 * console-interceptor.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

const browserConsole : Console = console;

type ConsoleEvent = "clear" | "log" | "error" | "warn" | "info" | "table";

export const CONSOLE_HISTORY : Array<[ConsoleEvent, number, any[]]> = [];
const HISTORY_LIMIT : number = 100;

function consoleOp (type : ConsoleEvent, args : any[]) : void
{
    CONSOLE_HISTORY.push([type, Date.now(), args]);
    if (CONSOLE_HISTORY.length > HISTORY_LIMIT)
    {
        CONSOLE_HISTORY.shift();
    }
    (browserConsole as {[key : string] : any})[type]
        .apply(browserConsole, args);
}

function boundConsoleFunction (name : string) : (...anything : any[]) => void
{
    if (name in browserConsole)
    {
        return (browserConsole as any)[name].bind(browserConsole);
    }
    return () : void => null;
}

console = {
    // First just rebind all of the functions we don't care about.
    // We may care about more of these in the future (time/count/group/etc)
    assert :            boundConsoleFunction("assert"),
    count :             boundConsoleFunction("count"),
    countReset :        boundConsoleFunction("countReset"),
    debug :             boundConsoleFunction("debug"),
    dir :               boundConsoleFunction("dir"),
    dirxml :            boundConsoleFunction("dirxml"),
    exception :         boundConsoleFunction("exception"),
    group :             boundConsoleFunction("group"),
    groupCollapsed :    boundConsoleFunction("groupCollapsed"),
    groupEnd :          boundConsoleFunction("groupEnd"),
    markTimeline :      boundConsoleFunction("markTimeline"),
    profile :           boundConsoleFunction("profile"),
    profileEnd :        boundConsoleFunction("profileEnd"),
    select :            boundConsoleFunction("select"),
    time :              boundConsoleFunction("time"),
    timeEnd :           boundConsoleFunction("timeEnd"),
    timeStamp :         boundConsoleFunction("timeStamp"),
    timeline :          boundConsoleFunction("timeline"),
    timelineEnd :       boundConsoleFunction("timelineEnd"),
    trace :             boundConsoleFunction("trace"),

    msIsIndependentlyComposed : () => false,
    memory : browserConsole.memory,

    // For functions we wish to record, we use the consoleOp function to push
    // a record to our history array and then use Function.apply to call the
    // original function with the full arguments.
    clear () : void
    {
        consoleOp("clear", Array.from(arguments));
    },

    log (message? : any, ...optionalParams : any[]) : void
    {
        consoleOp("log", Array.from(arguments));
    },

    warn (message? : any, ...optionalParams : any[]) : void
    {
        consoleOp("warn", Array.from(arguments));
    },

    error (message? : any, ...optionalParams : any[]) : void
    {
        consoleOp("error", Array.from(arguments));
    },

    info (message? : any, ...optionalParams : any[]) : void
    {
        consoleOp("info", Array.from(arguments));
    },

    table (...data : any[]) : void
    {
        consoleOp("table", Array.from(arguments));
    },

    // weird @types/node fix
    Console: browserConsole.Console,
};



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/debug/console-interceptor.ts